import React, { useEffect, useState, useRef, useCallback } from 'react';
import { Drawer, IconButton, Typography, Chip, CircularProgress } from '@mui/material';
import { X, Paperclip, Trash2, Send, AtSign } from 'lucide-react';
import {
    fetchComments,
    createComment,
    updateComment,
    deleteComment,
    uploadCommentAttachment,
    downloadFile,
    fetchUsersForMentions,
    type UserOption
} from '../../services/commentsService';
import { Comment } from '../../types/comment';
import AvatarImage from '../AvatarImage';
import UserMentionPopover from '../UserMentionPopover';
import { formatDateTime } from '../../lib/utils';
import { showErrorModal, showSuccessModal, showConfirmDeleteModal, showLoadingModal, closeLoadingModal } from '../ui/ConfirmDialog';

export interface CommentsDrawerProps {
    open: boolean;
    onClose: () => void;
    entityId?: string; // CodeSheet ID (formatId)
    codeItem?: string; // Code Item (e.g., "C01", "C02") - will be sent as 'block'
    optionCode?: string; // Option Code (e.g., "AWS", "092CS") - will be sent as 'section'
    currentUserId?: string;
    currentUserName?: string;
    canManageComments?: boolean;
    accessMessage?: string | null;
    onCommentClick?: (block: string, section: string) => void; // Callback when clicking on a comment to highlight the row
}

export default function CommentsDrawer({
    open,
    onClose,
    entityId,
    codeItem,
    optionCode,
    currentUserId,
    currentUserName,
    canManageComments = true,
    accessMessage = null,
    onCommentClick
}: CommentsDrawerProps) {
    const [comments, setComments] = useState<Comment[]>([]);
    const [loading, setLoading] = useState(false);
    const [newCommentText, setNewCommentText] = useState('');
    const [editingCommentId, setEditingCommentId] = useState<string | null>(null);
    const [editingText, setEditingText] = useState('');
    const [submitting, setSubmitting] = useState(false);
    const [attachments, setAttachments] = useState<string[]>([]);
    const [uploadingAttachment, setUploadingAttachment] = useState(false);
    const [taggedUserIds, setTaggedUserIds] = useState<string[]>([]);
    const [showMentions, setShowMentions] = useState(false);
    const [mentionSearch, setMentionSearch] = useState('');
    const [mentionPosition, setMentionPosition] = useState(0);
    const [userOptions, setUserOptions] = useState<UserOption[]>([]);
    const [loadingUsers, setLoadingUsers] = useState(false);

    const fileInputRef = useRef<HTMLInputElement>(null);
    const textareaRef = useRef<HTMLTextAreaElement>(null);
    const editableRef = useRef<HTMLDivElement>(null);
    const mentionsDropdownRef = useRef<HTMLDivElement>(null);

    // Load comments when drawer opens
    useEffect(() => {
        if (open && entityId) {
            loadComments();
        }
    }, [open, entityId]);

    // Load users for mentions
    useEffect(() => {
        if (open) {
            loadUsers();
        }
    }, [open]);

    // Close mentions dropdown when clicking outside
    useEffect(() => {
        function handleClickOutside(e: MouseEvent) {
            if (mentionsDropdownRef.current && !mentionsDropdownRef.current.contains(e.target as Node)) {
                setShowMentions(false);
            }
        }
        if (showMentions) {
            document.addEventListener('mousedown', handleClickOutside);
            return () => document.removeEventListener('mousedown', handleClickOutside);
        }
    }, [showMentions]);

    const loadComments = async () => {
        setLoading(true);
        try {
            const response = await fetchComments({
                EntityId: entityId,
                Page: 1,
                PageSize: 100,
                OrderByField: 'commentDate',
                Descending: true
            });
            setComments(response.results || []);
        } catch (error) {
            console.error('Failed to load comments', error);
            await showErrorModal({
                title: 'Error Loading Comments',
                description: 'Failed to load comments. Please try again.',
                confirmText: 'OK'
            });
        } finally {
            setLoading(false);
        }
    };

    const loadUsers = async () => {
        setLoadingUsers(true);
        try {
            const usersList = await fetchUsersForMentions();
            setUserOptions(usersList);
        } catch (error) {
            console.error('Failed to load users', error);
            setUserOptions([]);
        } finally {
            setLoadingUsers(false);
        }
    };

    const escapeHtml = useCallback((text: string): string => {
        const div = document.createElement('div');
        div.textContent = text;
        return div.innerHTML;
    }, []);

    // Extract plain text from contenteditable (including mention markers)
    const extractTextFromEditor = useCallback((element: HTMLDivElement): string => {
        let text = '';
        const nodes = element.childNodes;
        
        nodes.forEach(node => {
            if (node.nodeType === Node.TEXT_NODE) {
                text += node.textContent || '';
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                const el = node as HTMLElement;
                if (el.classList.contains('mention-chip')) {
                    const userName = el.getAttribute('data-username');
                    text += `@[${userName}]`;
                } else {
                    text += el.textContent || '';
                }
            }
        });
        
        return text;
    }, []);

    // Render content with chips in contenteditable
    const renderEditableContent = useCallback((text: string): string => {
        const mentionRegex = /@\[([^\]]+)\]/g;
        let html = '';
        let lastIndex = 0;
        let match;

        while ((match = mentionRegex.exec(text)) !== null) {
            const userName = match[1];
            const user = userOptions.find(u => u.name === userName);
            
            // Add text before mention
            html += escapeHtml(text.substring(lastIndex, match.index));
            
            // Add mention chip
            html += `<span class="mention-chip" contenteditable="false" data-username="${escapeHtml(userName)}" data-userid="${user?.id || ''}" style="display: inline-flex; align-items: center; background-color: #E3F2FD; color: #1D5176; border-radius: 12px; padding: 2px 8px; margin: 0 2px; font-size: 13px; font-weight: 500; cursor: pointer;">@${escapeHtml(userName)}<span class="remove-mention" data-username="${escapeHtml(userName)}" style="margin-left: 4px; cursor: pointer; font-weight: bold; opacity: 0.7; hover:opacity: 1;">×</span></span>`;
            
            lastIndex = match.index + match[0].length;
        }
        
        // Add remaining text
        html += escapeHtml(text.substring(lastIndex));
        
        return html || '<span style="color: #9ca3af;">Type @ to mention someone...</span>';
    }, [userOptions, escapeHtml]);

    const handleEditableInput = () => {
        if (!editableRef.current) return;
        
        const text = extractTextFromEditor(editableRef.current);
        setNewCommentText(text);
        
        // Sync tagged users with mentions in text
        const mentionRegex = /@\[([^\]]+)\]/g;
        const mentionsInText: string[] = [];
        let match;
        
        while ((match = mentionRegex.exec(text)) !== null) {
            const userName = match[1].trim();
            const user = userOptions.find(u => u.name === userName);
            if (user && !mentionsInText.includes(user.id)) {
                mentionsInText.push(user.id);
            }
        }
        
        if (JSON.stringify(mentionsInText.sort()) !== JSON.stringify([...taggedUserIds].sort())) {
            setTaggedUserIds(mentionsInText);
        }

        // Check for @ mentions for dropdown
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return;
        
        const textContent = editableRef.current.textContent || '';
        const cursorPos = getCursorPosition(editableRef.current);
        const textBeforeCursor = textContent.substring(0, cursorPos);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
            
            if (!textAfterAt.includes(' ') && !text.substring(lastAtIndex).match(/^@\[[^\]]+\]/)) {
                setMentionSearch(textAfterAt);
                setMentionPosition(lastAtIndex);
                setShowMentions(true);
            } else {
                setShowMentions(false);
            }
        } else {
            setShowMentions(false);
        }
    };

    const getCursorPosition = useCallback((element: HTMLDivElement): number => {
        const selection = window.getSelection();
        if (!selection || selection.rangeCount === 0) return 0;
        
        const range = selection.getRangeAt(0);
        const preCaretRange = range.cloneRange();
        preCaretRange.selectNodeContents(element);
        preCaretRange.setEnd(range.endContainer, range.endOffset);
        
        return preCaretRange.toString().length;
    }, []);

    const setCursorPosition = useCallback((element: HTMLDivElement, position: number) => {
        const selection = window.getSelection();
        const range = document.createRange();
        
        let currentPos = 0;
        
        const findPosition = (node: Node): boolean => {
            if (node.nodeType === Node.TEXT_NODE) {
                const length = node.textContent?.length || 0;
                if (currentPos + length >= position) {
                    range.setStart(node, position - currentPos);
                    range.collapse(true);
                    return true;
                }
                currentPos += length;
            } else if (node.nodeType === Node.ELEMENT_NODE) {
                for (let i = 0; i < node.childNodes.length; i++) {
                    if (findPosition(node.childNodes[i])) {
                        return true;
                    }
                }
            }
            return false;
        };
        
        if (!findPosition(element)) {
            range.selectNodeContents(element);
            range.collapse(false);
        }
        
        selection?.removeAllRanges();
        selection?.addRange(range);
    }, []);

    const handleTextChange = (text: string, isEditing: boolean = false) => {
        if (isEditing) {
            setEditingText(text);
        } else {
            setNewCommentText(text);
        }

        // Sync tagged users with mentions in text
        const mentionRegex = /@\[([^\]]+)\]/g;
        const mentionsInText: string[] = [];
        let match;
        
        while ((match = mentionRegex.exec(text)) !== null) {
            const userName = match[1].trim();
            const user = userOptions.find(u => u.name === userName);
            if (user && !mentionsInText.includes(user.id)) {
                mentionsInText.push(user.id);
            }
        }
        
        // Update taggedUserIds only if it has changed
        if (JSON.stringify(mentionsInText.sort()) !== JSON.stringify([...taggedUserIds].sort())) {
            setTaggedUserIds(mentionsInText);
        }

        // Check for @ mentions for dropdown
        const cursorPos = textareaRef.current?.selectionStart || 0;
        const textBeforeCursor = text.substring(0, cursorPos);
        const lastAtIndex = textBeforeCursor.lastIndexOf('@');

        if (lastAtIndex !== -1) {
            const textAfterAt = textBeforeCursor.substring(lastAtIndex + 1);
            // Check if we're starting a mention (not already in brackets)
            const beforeAt = text.substring(0, lastAtIndex);
            const afterAt = text.substring(lastAtIndex);
            
            // Check if this @ is not part of a complete mention @[name]
            const isCompleteMention = afterAt.match(/^@\[[^\]]+\]/);
            
            // Check if there's a space or bracket after @, if so, close mentions
            if (textAfterAt.includes(' ') || textAfterAt.includes('[') || isCompleteMention) {
                setShowMentions(false);
            } else {
                setMentionSearch(textAfterAt);
                setMentionPosition(lastAtIndex);
                setShowMentions(true);
            }
        } else {
            setShowMentions(false);
        }
    };

    const insertMention = (user: UserOption) => {
        if (editingCommentId) {
            // For editing mode, use old textarea behavior
            const text = editingText;
            const beforeMention = text.substring(0, mentionPosition);
            const afterMention = text.substring(mentionPosition + mentionSearch.length + 1);
            const newText = `${beforeMention}@[${user.name}] ${afterMention}`;
            setEditingText(newText);
            setTimeout(() => textareaRef.current?.focus(), 0);
        } else {
            // For new comment, use contenteditable
            if (!editableRef.current) return;
            
            const text = newCommentText;
            const beforeMention = text.substring(0, mentionPosition);
            const afterMention = text.substring(mentionPosition + mentionSearch.length + 1);
            const newText = `${beforeMention}@[${user.name}] ${afterMention}`;
            
            setNewCommentText(newText);
            
            // Update the contenteditable display
            setTimeout(() => {
                if (editableRef.current) {
                    const cursorPos = beforeMention.length + user.name.length + 4; // @[name] + space
                    editableRef.current.innerHTML = renderEditableContent(newText);
                    setCursorPosition(editableRef.current, cursorPos);
                    editableRef.current.focus();
                }
            }, 0);
        }

        // Add to tagged users if not already tagged
        if (!taggedUserIds.includes(user.id)) {
            setTaggedUserIds([...taggedUserIds, user.id]);
        }

        setShowMentions(false);
        setMentionSearch('');
    };

    const filteredUsers = userOptions.filter(u =>
        mentionSearch.trim() === '' ||
        u.name.toLowerCase().includes(mentionSearch.toLowerCase()) ||
        u.emailAddress.toLowerCase().includes(mentionSearch.toLowerCase())
    );

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (!canManageComments) return;
        const files = e.target.files;
        if (!files || files.length === 0) return;

        setUploadingAttachment(true);
        try {
            const uploadPromises = Array.from(files).map(file => uploadCommentAttachment(file));
            const uploadedFileNames = await Promise.all(uploadPromises);
            setAttachments([...attachments, ...uploadedFileNames]);
        } catch (error) {
            console.error('Failed to upload attachment', error);
            await showErrorModal({
                title: 'Upload Failed',
                description: 'Failed to upload attachment. Please try again.',
                confirmText: 'OK'
            });
        } finally {
            setUploadingAttachment(false);
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        }
    };

    const handleRemoveAttachment = (index: number) => {
        setAttachments(attachments.filter((_, i) => i !== index));
    };

    const handleSubmitComment = async () => {
        if (!canManageComments) {
            if (accessMessage) {
                await showErrorModal({
                    title: 'Unable to Add Comment',
                    description: accessMessage,
                    confirmText: 'OK'
                });
            }
            return;
        }
        if (!newCommentText.trim() && attachments.length === 0) return;

        setSubmitting(true);
        
        // Show loading modal
        showLoadingModal({
            title: 'Posting Comment',
            message: 'Please wait while your comment is being posted...'
        });

        try {
            await createComment({
                entityId: entityId || '',
                userId: currentUserId || '',
                commentDate: new Date().toISOString(),
                text: newCommentText.trim(),
                block: codeItem || null,
                section: optionCode || null,
                attachments,
                taggedUserIds,
                id: ''
            });

            setNewCommentText('');
            setAttachments([]);
            setTaggedUserIds([]);
            
            // Clear contenteditable
            if (editableRef.current) {
                editableRef.current.innerHTML = '';
            }
            
            await loadComments();

            // Close loading modal
            closeLoadingModal();

            await showSuccessModal({
                title: 'Comment Posted Successfully',
                description: 'Your comment has been added successfully.',
                timer: 1500,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Failed to create comment', error);
            
            // Close loading modal before showing error
            closeLoadingModal();
            
            await showErrorModal({
                title: 'Error',
                description: 'Failed to add comment. Please try again.',
                confirmText: 'OK'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleEditComment = (comment: Comment) => {
        if (!canManageComments) return;
        setEditingCommentId(comment.id);
        setEditingText(comment.text);
    };

    const handleSaveEdit = async (commentId: string) => {
        if (!canManageComments) return;
        if (!editingText.trim()) return;

        setSubmitting(true);
        try {
            const comment = comments.find(c => c.id === commentId);
            if (!comment) return;

            await updateComment(commentId, {
                ...comment,
                text: editingText.trim(),
                updatedAt: new Date().toISOString()
            });

            setEditingCommentId(null);
            setEditingText('');
            await loadComments();

            await showSuccessModal({
                title: 'Comment Updated',
                timer: 1200,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Failed to update comment', error);
            await showErrorModal({
                title: 'Error',
                description: 'Failed to update comment. Please try again.',
                confirmText: 'OK'
            });
        } finally {
            setSubmitting(false);
        }
    };

    const handleCancelEdit = () => {
        setEditingCommentId(null);
        setEditingText('');
    };

    const handleDeleteComment = async (commentId: string) => {
        if (!canManageComments) return;
        const confirmed = await showConfirmDeleteModal({
            entityLabel: 'Comment',
            title: 'Delete Comment',
            description: 'This action will permanently remove this comment. Do you want to proceed?',
            confirmText: 'Delete',
            cancelText: 'Cancel'
        });

        if (!confirmed) return;

        try {
            await deleteComment(commentId);
            await loadComments();

            await showSuccessModal({
                title: 'Comment Deleted',
                timer: 1200,
                showConfirmButton: false
            });
        } catch (error) {
            console.error('Failed to delete comment', error);
            await showErrorModal({
                title: 'Error',
                description: 'Failed to delete comment. Please try again.',
                confirmText: 'OK'
            });
        }
    };

    const renderCommentText = (text: string, taggedUserIds: string[], taggedUsers: any[]) => {
        const userNameToId = new Map<string, string>();
        
        if (taggedUsers && taggedUsers.length > 0) {
            taggedUsers.forEach(user => {
                if (user.name && user.id) {
                    userNameToId.set(user.name, user.id);
                }
            });
        }

        const parts: (string | React.ReactNode)[] = [];
        let lastIndex = 0;
        // Updated regex to match @[userName] format with brackets as delimiters
        const mentionRegex = /@\[([^\]]+)\]/g;
        let match;
        let foundMentions = false;
        let mentionIndex = 0;

        while ((match = mentionRegex.exec(text)) !== null) {
            foundMentions = true;
            const mentionStart = match.index;
            const fullMention = match[0]; // @[userName]
            const userName = match[1].trim(); // userName without brackets

            let userId = userNameToId.get(userName);

            if (!userId && taggedUserIds && taggedUserIds.length > mentionIndex) {
                userId = taggedUserIds[mentionIndex];
            }

            if (mentionStart > lastIndex) {
                parts.push(text.substring(lastIndex, mentionStart));
            }

            if (userId) {
                parts.push(
                    <UserMentionPopover key={`mention-${mentionStart}-${userId}`} userId={userId}>
                        @{userName}
                    </UserMentionPopover>
                );
            } else {
                parts.push(
                    <span
                        key={`mention-${mentionStart}`}
                        className="text-[#0078d4]"
                    >
                        @{userName}
                    </span>
                );
            }

            lastIndex = mentionStart + fullMention.length;
            mentionIndex++;
        }

        if (!foundMentions) {
            return text;
        }

        if (lastIndex < text.length) {
            parts.push(text.substring(lastIndex));
        }

        return <>{parts}</>;
    };

    // Initialize and sync contenteditable when newCommentText changes externally
    useEffect(() => {
        if (editableRef.current) {
            const isActive = document.activeElement === editableRef.current;
            if (!isActive || newCommentText === '') {
                const currentText = extractTextFromEditor(editableRef.current);
                if (currentText !== newCommentText) {
                    const cursorPos = isActive ? getCursorPosition(editableRef.current) : 0;
                    editableRef.current.innerHTML = renderEditableContent(newCommentText);
                    if (isActive && cursorPos > 0) {
                        setTimeout(() => {
                            if (editableRef.current) {
                                setCursorPosition(editableRef.current, cursorPos);
                            }
                        }, 0);
                    }
                }
            }
        }
    }, [newCommentText, userOptions, extractTextFromEditor, getCursorPosition, renderEditableContent, setCursorPosition]);

    return (
        <Drawer
            anchor="right"
            open={open}
            onClose={onClose}
            sx={{ '& .MuiDrawer-paper': { width: '34vw', minWidth: 360, maxWidth: 560, boxSizing: 'border-box' } }}
        >
            <div className="h-full flex flex-col bg-white">
                <div className="bg-[#E3F2FD] h-1"></div>

                {/* Header */}
                <div className="bg-white px-4 py-3 flex items-center justify-between border-b border-gray-200">
                    <Typography variant="h5" className="font-semibold text-gray-900">
                        Comments
                    </Typography>
                    <IconButton onClick={onClose} className="text-gray-500 hover:text-gray-700">
                        <X className="h-5 w-5" />
                    </IconButton>
                </div>

                {/* Comments List */}
                <div className="flex-1 overflow-auto p-4 bg-gray-50">
                    {loading ? (
                        <div className="flex justify-center items-center h-full">
                            <CircularProgress size={32} />
                        </div>
                    ) : comments.length === 0 ? (
                        <div className="flex flex-col items-center justify-center h-full text-gray-500">
                            <Typography variant="body2" className="text-center">
                                No comments yet. Be the first to comment!
                            </Typography>
                        </div>
                    ) : (
                        <div className="space-y-4">
                            {comments.map((comment) => (
                                <div key={comment.id} className="bg-white rounded-lg p-3 shadow-sm border border-gray-200">
                                    {/* Comment Header */}
                                    <div className="flex items-start gap-3 mb-2">
                                        <AvatarImage
                                            name={comment.createdBy?.name || 'User'}
                                            filename={comment.createdBy?.avatarUri || null}
                                            size={32}
                                        />
                                        <div className="flex-1 min-w-0">
                                            <div className="flex items-center justify-between mb-1">
                                                <Typography variant="subtitle2" className="font-semibold text-gray-900 text-sm">
                                                    {comment.createdBy?.name || 'Unknown User'}
                                                </Typography>
                                                <Typography variant="caption" className="text-gray-500 text-xs">
                                                    {formatDateTime(comment.commentDate)}
                                                </Typography>
                                            </div>

                                            {/* Block and Section Badges */}
                                            {(comment.block || comment.section) && (
                                                <div className="flex items-center gap-1.5 mb-2">
                                                    {comment.block && (
                                                        <span 
                                                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#1D5176] text-white"
                                                            /* TEMPORARILY DISABLED - Re-enable later
                                                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#1D5176] text-white cursor-pointer hover:bg-[#164159] transition-colors"
                                                            onClick={() => {
                                                                if (onCommentClick && comment.block && comment.section) {
                                                                    onCommentClick(comment.block, comment.section);
                                                                }
                                                            }}
                                                            title="Click to highlight in table"
                                                            */
                                                        >
                                                            {comment.block}
                                                        </span>
                                                    )}
                                                    {comment.section && (
                                                        <span 
                                                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#E3F2FD] text-[#1D5176]"
                                                            /* TEMPORARILY DISABLED - Re-enable later
                                                            className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-[#E3F2FD] text-[#1D5176] cursor-pointer hover:bg-[#BBDEFB] transition-colors"
                                                            onClick={() => {
                                                                if (onCommentClick && comment.block && comment.section) {
                                                                    onCommentClick(comment.block, comment.section);
                                                                }
                                                            }}
                                                            title="Click to highlight in table"
                                                            */
                                                        >
                                                            Option {comment.section}
                                                        </span>
                                                    )}
                                                </div>
                                            )}

                                            {/* Comment Text */}
                                            {editingCommentId === comment.id ? (
                                                <div className="mt-2">
                                                    <textarea
                                                        ref={textareaRef}
                                                        value={editingText}
                                                        onChange={(e) => handleTextChange(e.target.value, true)}
                                                        disabled={!canManageComments}
                                                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical min-h-[60px] font-sans"
                                                        placeholder="Edit your comment... Type @ to mention someone"
                                                        style={{ lineHeight: '1.5' }}
                                                    />
                                                    <div className="flex gap-2 mt-2">
                                                        <button
                                                            onClick={() => handleSaveEdit(comment.id)}
                                                            disabled={submitting || !canManageComments}
                                                            className="px-3 py-1 text-xs bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50"
                                                        >
                                                            Save
                                                        </button>
                                                        <button
                                                            onClick={handleCancelEdit}
                                                            className="px-3 py-1 text-xs bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                                                        >
                                                            Cancel
                                                        </button>
                                                    </div>
                                                </div>
                                            ) : (
                                                <Typography variant="body2" className="text-gray-700 text-sm mt-1 whitespace-pre-wrap">
                                                    {renderCommentText(comment.text, comment.taggedUserIds || [], comment.taggedUsers || [])}
                                                </Typography>
                                            )}

                                            {/* Tagged Users */}
                                            {comment.taggedUsers && comment.taggedUsers.length > 0 && (
                                                <div className="flex flex-wrap gap-1 mt-2">
                                                    {comment.taggedUsers.map((user) => (
                                                        <Chip
                                                            key={user.id}
                                                            label={user.name}
                                                            size="small"
                                                            variant="outlined"
                                                            sx={{ fontSize: '11px', height: '20px' }}
                                                        />
                                                    ))}
                                                </div>
                                            )}

                                            {/* Attachments */}
                                            {comment.attachments && comment.attachments.length > 0 && (
                                                <div className="mt-2 space-y-1">
                                                    {comment.attachments.map((filename, idx) => (
                                                        <div key={idx} className="flex items-center gap-2 text-xs text-gray-600">
                                                            <Paperclip className="h-3 w-3" />
                                                            <button
                                                                onClick={async () => {
                                                                    try {
                                                                        const blob = await downloadFile(filename);
                                                                        const url = window.URL.createObjectURL(blob);
                                                                        const a = document.createElement('a');
                                                                        a.href = url;
                                                                        a.download = filename;
                                                                        document.body.appendChild(a);
                                                                        a.click();
                                                                        window.URL.revokeObjectURL(url);
                                                                        document.body.removeChild(a);
                                                                    } catch (err) {
                                                                        console.error('Download failed', err);
                                                                    }
                                                                }}
                                                                className="hover:underline truncate max-w-[200px]"
                                                            >
                                                                {filename}
                                                            </button>
                                                        </div>
                                                    ))}
                                                </div>
                                            )}

                                            {/* Actions */}
                                            {editingCommentId !== comment.id && comment.userId === currentUserId && (
                                                <div className="flex gap-3 mt-2">
                                                    <button
                                                        onClick={() => handleEditComment(comment)}
                                                        disabled={!canManageComments}
                                                        className="text-xs text-blue-600 hover:text-blue-800 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        Edit
                                                    </button>
                                                    <button
                                                        onClick={() => handleDeleteComment(comment.id)}
                                                        disabled={!canManageComments}
                                                        className="text-xs text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-50"
                                                    >
                                                        Delete
                                                    </button>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>

                {/* Add Comment Section */}
                <div className="bg-white border-t border-gray-200 p-4">
                    <Typography variant="subtitle2" className="font-semibold text-gray-900 mb-2 text-sm">
                        Add a comment
                    </Typography>
                    {!canManageComments && accessMessage && (
                        <div className="mb-2 rounded border border-[#C9DDF1] bg-[#F2F6F9] px-3 py-2 text-xs text-[#1D5176]">
                            {accessMessage}
                        </div>
                    )}

                    <div className="relative">
                        <div
                            ref={editableRef}
                            contentEditable={canManageComments}
                            onInput={handleEditableInput}
                            onClick={(e) => {
                                const target = e.target as HTMLElement;
                                if (target.classList.contains('remove-mention')) {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    const userName = target.getAttribute('data-username');
                                    if (userName) {
                                        const mentionPattern = new RegExp(`@\\[${userName.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}\\]`, 'g');
                                        const newText = newCommentText.replace(mentionPattern, '');
                                        setNewCommentText(newText);
                                        if (editableRef.current) {
                                            const cursorPos = getCursorPosition(editableRef.current);
                                            editableRef.current.innerHTML = renderEditableContent(newText);
                                            setTimeout(() => {
                                                if (editableRef.current) {
                                                    setCursorPosition(editableRef.current, Math.max(0, cursorPos - userName.length - 3));
                                                    editableRef.current.focus();
                                                }
                                            }, 0);
                                        }
                                    }
                                }
                            }}
                            onKeyDown={(e) => {
                                if (e.key === 'Enter' && !e.shiftKey && !showMentions) {
                                    e.preventDefault();
                                    handleSubmitComment();
                                }
                            }}
                            className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 resize-vertical min-h-[80px] font-sans overflow-auto"
                            style={{ 
                                lineHeight: '1.5',
                                outline: 'none',
                                backgroundColor: canManageComments ? undefined : '#F9FAFB'
                            }}
                            suppressContentEditableWarning
                        />

                        {/* Mentions Dropdown */}
                        {showMentions && (
                            <div
                                ref={mentionsDropdownRef}
                                className="absolute bottom-full left-0 mb-1 w-full bg-white border border-gray-300 rounded shadow-lg max-h-48 overflow-auto z-50"
                            >
                                {loadingUsers ? (
                                    <div className="p-2 text-center text-gray-500 text-xs">Loading users...</div>
                                ) : filteredUsers.length === 0 ? (
                                    <div className="p-2 text-center text-gray-500 text-xs">No users found</div>
                                ) : (
                                    filteredUsers.map((user) => (
                                        <button
                                            key={user.id}
                                            onClick={() => insertMention(user)}
                                            className="w-full flex items-center gap-2 px-3 py-2 hover:bg-gray-100 text-left"
                                        >
                                            <AvatarImage name={user.name} filename={user.avatarUri || null} size={24} />
                                            <div className="flex-1 min-w-0">
                                                <div className="text-sm font-medium text-gray-900 truncate">{user.name}</div>
                                                <div className="text-xs text-gray-500 truncate">{user.emailAddress}</div>
                                            </div>
                                        </button>
                                    ))
                                )}
                            </div>
                        )}
                    </div>

                    {/* Attachments */}
                    {attachments.length > 0 && (
                        <div className="mt-2 space-y-1">
                            {attachments.map((filename, idx) => (
                                <div key={idx} className="flex items-center justify-between text-xs bg-gray-50 px-2 py-1 rounded">
                                    <div className="flex items-center gap-2 truncate">
                                        <Paperclip className="h-3 w-3 text-gray-500" />
                                        <span className="truncate">{filename}</span>
                                    </div>
                                        <button
                                            onClick={() => handleRemoveAttachment(idx)}
                                            disabled={!canManageComments}
                                            className="text-red-600 hover:text-red-800 disabled:cursor-not-allowed disabled:opacity-50"
                                        >
                                            <Trash2 className="h-3 w-3" />
                                        </button>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* Actions */}
                    <div className="flex items-center justify-between mt-3">
                        <div className="flex gap-2">
                            <input
                                ref={fileInputRef}
                                type="file"
                                multiple
                                onChange={handleFileUpload}
                                className="hidden"
                            />
                            <button
                                onClick={() => fileInputRef.current?.click()}
                                disabled={uploadingAttachment || !canManageComments}
                                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-50"
                                title={canManageComments ? "Attach files" : (accessMessage || "You do not have permission to perform this action.")}
                            >
                                <Paperclip className="h-4 w-4" />
                            </button>
                            <button
                                onClick={() => {
                                    setShowMentions(true);
                                    editableRef.current?.focus();
                                }}
                                disabled={!canManageComments}
                                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded disabled:opacity-50"
                                title={canManageComments ? "Mention user" : (accessMessage || "You do not have permission to perform this action.")}
                            >
                                <AtSign className="h-4 w-4" />
                            </button>
                        </div>

                        <button
                            onClick={handleSubmitComment}
                            disabled={submitting || !canManageComments || (!newCommentText.trim() && attachments.length === 0)}
                            className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                            <Send className="h-4 w-4" />
                            Send
                        </button>
                    </div>
                </div>
            </div>
        </Drawer>
    );
}
